import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import {
  type FilterDataWithLabels,
  useNewsFeedFilterDataWithLabels,
} from "@/store/news-feed/filters/newsFeed-selectors";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import NewsFeedFilterSection from "./NewsFeedFilterSection";

/**
 * Container component for static filters with predefined enum values
 * Includes: Prominence, Tier, Sentiment, Category, Score, Social Engagement,
 * Domain Authority, Readership, Region, Country, and Language filters
 */
const NewsFeedStaticFiltersContainer = () => {
  const prominenceFilters = useNewsFeedFilterDataWithLabels("PROMINENCE");
  const tierFilters = useNewsFeedFilterDataWithLabels("TIER");
  const sentimentFilters = useNewsFeedFilterDataWithLabels("SENTIMENT");
  const categoryFilters = useNewsFeedFilterDataWithLabels("CATEGORY");
  const scoreFilters = useNewsFeedFilterDataWithLabels("SCORE_BUCKETS");
  const socialFilters = useNewsFeedFilterDataWithLabels("SOCIAL_BUCKETS");
  const domainFilters = useNewsFeedFilterDataWithLabels(
    "DOMAIN_AUTHORITY_BUCKETS",
  );
  const readershipFilters =
    useNewsFeedFilterDataWithLabels("READERSHIP_BUCKETS");
  const regionFilters = useNewsFeedFilterDataWithLabels("REGION");
  const countryFilters = useNewsFeedFilterDataWithLabels("COUNTRY");
  const languageFilters = useNewsFeedFilterDataWithLabels("LANGUAGE");

  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );
  const setCategoryExpansion = useNewsFeedFilters(
    (state) => state.setCategoryExpansion,
  );

  const { toggleFilterSelection, selectedFilters } =
    useAnalysisSidebarFilterParams();

  const filterConfigs: {
    title: string;
    category: ShareVoiceSidebarCategory;
    filterData: FilterDataWithLabels;
  }[] = [
    {
      title: "LLM Impact Score",
      category: "SCORE_BUCKETS",
      filterData: scoreFilters,
    },
    {
      title: "Prominence",
      category: "PROMINENCE",
      filterData: prominenceFilters,
    },
    {
      title: "AI Sentiment",
      category: "SENTIMENT",
      filterData: sentimentFilters,
    },
    {
      title: "Publication Tier",
      category: "TIER",
      filterData: tierFilters,
    },
    {
      title: "Social Engagement",
      category: "SOCIAL_BUCKETS",
      filterData: socialFilters,
    },
    {
      title: "Domain Authority",
      category: "DOMAIN_AUTHORITY_BUCKETS",
      filterData: domainFilters,
    },
    {
      title: "Readership",
      category: "READERSHIP_BUCKETS",
      filterData: readershipFilters,
    },
    {
      title: "Content Category",
      category: "CATEGORY",
      filterData: categoryFilters,
    },
    {
      title: "Region",
      category: "REGION",
      filterData: regionFilters,
    },
    {
      title: "Country",
      category: "COUNTRY",
      filterData: countryFilters,
    },
    {
      title: "Language",
      category: "LANGUAGE",
      filterData: languageFilters,
    },
  ];

  return (
    <>
      {filterConfigs.map(({ title, category, filterData }) => (
        <NewsFeedFilterSection
          key={category}
          title={title}
          category={category}
          filterData={filterData}
          selectedFilters={selectedFilters}
          onToggleFilter={toggleFilterSelection}
          isExpanded={expandedCategories.has(category)}
          onValueChange={(value) => {
            setCategoryExpansion(category, value === category);
          }}
        />
      ))}
    </>
  );
};

export default NewsFeedStaticFiltersContainer;
